import React from "react";
import { motion } from "framer-motion";

export default function TermsOfUse() {
  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-neutral-950 pb-16 transition-colors"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <header className="bg-white dark:bg-neutral-900 shadow-md border-b-4 border-blue-600 dark:border-blue-500 transition-colors">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14 md:py-16 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-800 dark:text-blue-300 mb-3 sm:mb-4">
            📜 Coinzetto – Terms of Use
          </h1>
          <p className="text-blue-700 dark:text-blue-300 text-base sm:text-lg md:text-xl">
            Effective Date: 23 May 2025
          </p>
          <p className="text-gray-600 dark:text-gray-300 mt-1 text-sm sm:text-base">
            Operator: Coinzetto Technologies Private Limited, [Insert Registered Office Address]
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 mt-8 sm:mt-12 md:mt-16 space-y-8 sm:space-y-10 md:space-y-12 text-gray-800 dark:text-gray-100 leading-relaxed transition-colors">
        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            1. Introduction
          </h2>
          <p className="mt-2 text-sm sm:text-base">
            Welcome to Coinzetto, a platform for trading, depositing, and withdrawing virtual digital assets (“VDAs”) and INR.
            These Terms of Use (“Terms”) govern your use of our website, mobile applications, and other services (collectively, the “Coinzetto Platform”).
          </p>
          <p className="mt-2 text-sm sm:text-base">
            By registering an account, accessing or using any part of the Platform, you acknowledge that you have read, understood,
            and agree to be bound by these Terms and our Privacy Policy.
          </p>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            2. Definitions
          </h2>
          <ul className="list-disc pl-5 sm:pl-6 space-y-1 mt-2 text-sm sm:text-base">
            <li><strong>“We”, “Us”, “Company”, or “Coinzetto”</strong> means Coinzetto Technologies Private Limited.</li>
            <li><strong>“You”, “User”, or “Client”</strong> refers to any person who accesses or uses the Coinzetto Platform.</li>
            <li><strong>“Crypto Assets” or “VDAs”</strong> means blockchain-based digital assets such as Bitcoin, Ethereum, USDT, etc.</li>
            <li><strong>“Fiat” or “INR”</strong> refers to Indian Rupees.</li>
            <li><strong>“Client Asset Account”</strong> means the account opened by a User on Coinzetto to access our services.</li>
            <li><strong>“Fiat Wallet”</strong> refers to the INR ledger maintained by the User within Coinzetto.</li>
            <li><strong>“Crypto Wallet”</strong> refers to your digital ledger balance on Coinzetto for supported crypto assets.</li>
            <li><strong>“Futures Wallet”</strong> (if applicable) refers to the account used to engage in perpetual futures trading on Coinzetto.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            3. Eligibility
          </h2>
          <ul className="list-disc pl-5 sm:pl-6 space-y-1 mt-2 text-sm sm:text-base">
            <li>You must be at least 18 years old.</li>
            <li>You must be a resident of India.</li>
            <li>You must complete KYC and comply with AML regulations.</li>
            <li>Only one account per user is allowed.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            4. Services Offered
          </h2>
          <p className="mt-2 text-sm sm:text-base"><strong>(a) Spot Trading:</strong> Coinzetto facilitates crypto-to-INR and crypto-to-crypto spot trading. Users can place market or limit orders on the platform.</p>
          <p className="text-sm sm:text-base"><strong>(b) Quick Trade:</strong> A simple swap feature allowing instant trades based on liquidity pools.</p>
          <p className="text-sm sm:text-base"><strong>(c) Crypto Deposit and Withdrawal:</strong> You can deposit and withdraw supported crypto assets. We are not liable for assets sent to the wrong address or via an unsupported network.</p>
          <p className="text-sm sm:text-base"><strong>(d) INR Deposit and Withdrawal:</strong> Only supported Indian bank accounts may be used. INR deposits/withdrawals are subject to bank processing timelines and internal checks.</p>
          <p className="text-sm sm:text-base"><strong>(e) Futures Trading (optional):</strong> If offered, perpetual contracts will be available through a separate Futures Wallet, requiring sufficient collateral and subject to liquidation.</p>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            5. User Obligations
          </h2>
          <ul className="list-disc pl-5 sm:pl-6 space-y-1 mt-2 text-sm sm:text-base">
            <li>Maintain secure credentials and never share your login/password.</li>
            <li>Only deposit funds from your own verified bank account.</li>
            <li>Do not use Coinzetto for illegal purposes, money laundering, or fraud.</li>
            <li>Ensure tax compliance; we are not responsible for your tax obligations.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            6. Fees
          </h2>
          <p className="mt-2 text-sm sm:text-base">
            All applicable fees (trading, withdrawal, funding, liquidation, etc.) will be published at:&nbsp;
            <a
              href="https://coinzetto.com/fees"
              className="text-blue-600 dark:text-blue-400 underline underline-offset-2"
              target="_blank"
              rel="noreferrer"
            >
              https://coinzetto.com/fees
            </a>
          </p>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            7. Risk Disclosure
          </h2>
          <p className="mt-2 text-sm sm:text-base">
            Trading crypto assets involves significant risks, including market volatility and regulatory uncertainty.
            You may lose your entire investment. By using Coinzetto, you accept full responsibility for such risks.
          </p>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            8. KYC & AML
          </h2>
          <p className="mt-2 text-sm sm:text-base">
            We are fully compliant with India’s Prevention of Money Laundering Act (PMLA), and perform mandatory KYC for all users.
            We reserve the right to freeze or terminate accounts if suspicious activity is detected.
          </p>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            9. Account Suspension or Termination
          </h2>
          <p className="mt-2 text-sm sm:text-base">We may suspend or terminate your account without notice if:</p>
          <ul className="list-disc pl-5 sm:pl-6 space-y-1 mt-2 text-sm sm:text-base">
            <li>You violate these Terms.</li>
            <li>Your account is under investigation.</li>
            <li>Required by law enforcement or regulators.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            10. Intellectual Property
          </h2>
          <p className="mt-2 text-sm sm:text-base">
            All logos, content, and software on the Coinzetto Platform are owned or licensed by us.
            You may not use, copy, or distribute them without permission.
          </p>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            11. Privacy
          </h2>
          <p className="mt-2 text-sm sm:text-base">
            We process personal information in accordance with our Privacy Policy and applicable laws.
          </p>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            12. Liability Limitation
          </h2>
          <p className="mt-2 text-sm sm:text-base">To the maximum extent allowed by law:</p>
          <ul className="list-disc pl-5 sm:pl-6 space-y-1 mt-2 text-sm sm:text-base">
            <li>
              Coinzetto is not liable for losses due to market fluctuation, technical errors,
              regulatory changes, or user negligence.
            </li>
            <li>
              Our total liability shall not exceed the fees paid by you for the services in the previous 30 days.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            13. Indemnity
          </h2>
          <p className="mt-2 text-sm sm:text-base">
            You agree to indemnify Coinzetto from any claims, damages, or legal costs resulting from your use of
            the Platform or violation of these Terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            14. Governing Law & Jurisdiction
          </h2>
          <p className="mt-2 text-sm sm:text-base">
            These Terms are governed by the laws of India. Any dispute shall be subject to the exclusive jurisdiction
            of the courts in [Your State, e.g., Hyderabad, Telangana].
          </p>
        </section>

        <section>
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-800 dark:text-blue-300">
            15. Contact & Complaints
          </h2>
          <p className="mt-2 text-sm sm:text-base">
            If you have any questions or complaints, contact us via{" "}
            <a
              href="https://coinzetto.com/support"
              className="text-blue-600 dark:text-blue-400 underline underline-offset-2"
              target="_blank"
              rel="noreferrer"
            >
              https://coinzetto.com/support
            </a>.
          </p>
        </section>
      </main>
    </motion.div>
  );
}
